import { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * SygeforsikringDanmarkLogo
 * 
 * Viser det officielle logo for Sygeforsikringen "danmark".
 * Følger de officielle retningslinjer fra Sygeforsikringen "danmark":
 * 1. Logoet linker til www.sygeforsikring.dk (påkrævet af "danmark")
 * 2. Navnet staves altid: Sygeforsikringen "danmark" (med lille d og anførselstegn)
 * 3. Benytter "danmarks" officielle testlogo til brug for godkendelsesprocessen
 */
export function SygeforsikringDanmarkLogo({
  className = 'h-10 w-auto',
  asLink = true,
  href = 'https://www.sygeforsikring.dk',
  useTestLogo = true,
  alt = 'Sygeforsikringen "danmark" - logo'
}) {
  const [imgError, setImgError] = useState(false);

  const logoContent = (
    <div className="inline-flex items-center">
      {useTestLogo && !imgError ? (
        <img
          src="https://assets.auxodev.dk/assets/9bc67d49-c1ad-4360-8231-e9811640bce9/image_6cd063dc.png"
          alt={alt}
          className={`${className} object-contain`}
          loading="lazy"
          onError={() => setImgError(true)}
        />
      ) : (
        /* Vektor-fallback hvis billedet ikke kan indlæses */
        <svg
          viewBox="0 0 140 48"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={`${className} object-contain`}
          aria-label={alt}
          role="img"
        >
          {/* Rødt d-mærke med hjerte */}
          <path
            d="M24.7108 0V14.7565C22.4568 13.7617 20.0358 13.1813 17.4478 13.1813C7.84735 13.2642 0 21.057 0 30.5907C0 40.1244 7.84735 47.9171 17.5313 47.9171C20.0358 47.9171 22.5403 47.3368 24.7108 46.342V47.5026H36.8992V0H24.7108Z"
            fill="#CD1D28"
          />
          <path
            d="M18.3658 40.1241C17.2805 38.2174 13.9412 36.2278 13.9412 36.2278C7.84698 32.9946 8.34787 29.0982 8.34787 29.0982C8.76529 23.6267 13.5238 23.7925 13.5238 23.7925C16.8631 23.7925 18.3658 26.4454 18.3658 26.4454H18.4492C18.4492 26.4454 19.9519 23.7925 23.2912 23.7925C23.2912 23.7925 28.0497 23.6267 28.4671 29.0982C28.4671 29.0982 28.968 32.9117 22.8738 36.2278C22.7903 36.2278 19.451 38.3003 18.3658 40.1241Z"
            fill="white"
          />
          {/* 'danmark' typografi */}
          <path d="M53.0117 34.1554C52.2604 35.399 50.9247 35.9793 49.422 35.9793C45.9157 35.9793 44.2461 32.9948 44.2461 29.7617C44.2461 26.6943 45.9992 23.7927 49.422 23.7927C50.7577 23.7927 52.1769 24.373 52.8448 25.5337H52.9283V19.7306H56.1006V35.6477H53.0952V34.1554H53.0117ZM50.2568 26.1969C48.1698 26.1969 47.4184 28.0207 47.4184 29.8446C47.4184 31.6684 48.2533 33.5751 50.2568 33.5751C52.3439 33.5751 53.0117 31.7513 53.0117 29.8446C53.0117 27.9378 52.3439 26.1969 50.2568 26.1969Z" fill="#231F20"/>
          <path d="M58.6049 27.6062C58.7719 24.6218 61.4433 23.7098 64.0313 23.7098C66.3688 23.7098 69.1237 24.2072 69.1237 27.0259V32.9948C69.1237 34.0725 69.2072 35.0674 69.5411 35.5648H66.2853C66.2018 35.2332 66.1183 34.8186 66.0348 34.487C65.0331 35.5648 63.5304 35.8964 62.1112 35.8964C59.9406 35.8964 58.1875 34.8186 58.1875 32.4145C58.1875 29.8446 60.1911 29.1813 62.1112 28.9326C64.0313 28.601 65.8679 28.6839 65.8679 27.4404C65.8679 26.114 64.9496 25.8653 63.7808 25.8653C62.5286 25.8653 61.7772 26.3627 61.6938 27.6062H58.6049ZM65.9514 30.0104C65.4505 30.5078 64.2817 30.5078 63.2799 30.6736C62.2781 30.8394 61.3598 31.171 61.3598 32.3316C61.3598 33.4922 62.2781 33.8238 63.2799 33.8238C65.7844 33.8238 65.8679 31.8342 65.8679 31.171V30.0104H65.9514Z" fill="#231F20"/>
          <path d="M71.8789 24.1244H74.8843V25.6995H74.9678C75.8026 24.3731 77.1383 23.7928 78.5575 23.7928C81.9803 23.7928 82.8151 25.6995 82.8151 28.5181V35.6477H79.6428V29.0985C79.6428 27.1917 79.0584 26.2798 77.6392 26.2798C75.8861 26.2798 75.1347 27.2746 75.1347 29.5959V35.6477H71.9624V24.1244H71.8789Z" fill="#231F20"/>
          <path d="M85.5703 24.1244H88.5757V25.6995H88.6592C89.494 24.5389 90.7462 23.7928 92.2489 23.7928C93.6681 23.7928 95.0038 24.2902 95.6717 25.6995C96.3395 24.7047 97.5918 23.7928 99.1779 23.7928C101.682 23.7928 103.436 24.9534 103.436 27.9378V35.6477H100.263V29.0985C100.263 27.5233 100.096 26.2798 98.3431 26.2798C96.5065 26.2798 96.1726 27.772 96.1726 29.1814V35.6477H93.0002V29.1814C93.0002 27.8549 93.0837 26.2798 91.0801 26.2798C90.4123 26.2798 88.9096 26.6943 88.9096 28.9327V35.6477H85.7373V24.1244H85.5703Z" fill="#231F20"/>
          <path d="M105.94 27.6062C106.107 24.6218 108.778 23.7098 111.366 23.7098C113.704 23.7098 116.459 24.2072 116.459 27.0259V32.9948C116.459 34.0725 116.542 35.0674 116.876 35.5648H113.62C113.537 35.2332 113.453 34.8186 113.37 34.487C112.368 35.5648 110.865 35.8964 109.446 35.8964C107.276 35.8964 105.522 34.8186 105.522 32.4145C105.522 29.8446 107.526 29.1813 109.446 28.9326C111.366 28.601 113.203 28.6839 113.203 27.4404C113.203 26.114 112.285 25.8653 111.116 25.8653C109.864 25.8653 109.112 26.3627 109.029 27.6062H105.94ZM113.286 30.0104C112.785 30.5078 111.617 30.5078 110.615 30.6736C109.613 30.8394 108.695 31.171 108.695 32.3316C108.695 33.4922 109.613 33.8238 110.615 33.8238C113.119 33.8238 113.203 31.8342 113.203 31.171V30.0104H113.286Z" fill="#231F20"/>
          <path d="M119.213 24.1244H122.219V26.2798H122.302C122.887 24.7876 124.473 23.7928 125.975 23.7928C126.226 23.7928 126.476 23.8757 126.643 23.8757V26.8601C126.393 26.7772 125.892 26.7772 125.475 26.7772C123.137 26.7772 122.302 28.4352 122.302 30.5078V35.6477H119.13V24.1244H119.213Z" fill="#231F20"/>
          <path d="M128.479 19.6477H131.652V28.1865L135.659 24.0415H139.416L135.075 28.2694L139.917 35.5648H136.076L132.904 30.4249L131.652 31.5855V35.6477H128.479V19.6477Z" fill="#231F20"/>
          {/* 'SYGEFORSIKRINGEN' mindre tekst */}
          <path d="M46.2488 44.6839C46.2488 45.8445 47.0836 46.342 48.1689 46.342C49.3377 46.342 49.7551 45.7616 49.7551 45.1813C49.7551 44.601 49.4211 44.3523 49.0872 44.2694C48.5863 44.1036 47.835 43.9378 46.8332 43.6062C45.4975 43.2746 45.1635 42.4456 45.1635 41.6995C45.1635 40.2072 46.5827 39.4611 48.0019 39.4611C49.5881 39.4611 50.9238 40.2901 50.9238 41.8653H49.5881C49.5046 40.8704 48.8368 40.5388 47.9185 40.5388C47.3341 40.5388 46.4993 40.7875 46.4993 41.5337C46.4993 42.0311 46.8332 42.3627 47.4176 42.5285C47.501 42.5285 49.2542 43.0259 49.6716 43.1088C50.6734 43.4404 51.1743 44.2694 51.1743 45.0155C51.1743 46.7565 49.6716 47.4197 48.0854 47.4197C46.3323 47.4197 44.9131 46.5907 44.9131 44.6839H46.2488Z" fill="#231F20"/>
          <path d="M54.4302 44.2694L51.5918 39.6269H53.178L55.1815 43.0259L57.1851 39.6269H58.6878L55.8494 44.2694V47.2539H54.5137V44.2694H54.4302Z" fill="#231F20"/>
          <path d="M66.118 47.2539H65.2832L65.1162 46.342C64.3649 47.171 63.7805 47.4197 62.7787 47.4197C60.4412 47.4197 59.1055 45.5958 59.1055 43.4404C59.1055 41.285 60.4412 39.4611 62.7787 39.4611C64.4483 39.4611 65.8675 40.373 66.0345 42.0311H64.6988C64.5318 41.0363 63.697 40.5388 62.6952 40.5388C61.0256 40.5388 60.3577 41.9482 60.3577 43.4404C60.3577 44.9326 61.0256 46.342 62.6952 46.342C64.0309 46.342 64.7823 45.5129 64.8657 44.2694H62.8622V43.2746H66.118V47.2539Z" fill="#231F20"/>
          <path d="M67.7041 39.6269H73.2139V40.7875H69.0398V42.7772H72.88V43.8549H69.0398V46.0932H73.2974V47.2539H67.7041V39.6269Z" fill="#231F20"/>
          <path d="M74.5498 39.6269H79.8092V40.7875H75.8855V42.7772H79.3918V43.8549H75.8855V47.2539H74.5498V39.6269Z" fill="#231F20"/>
          <path d="M87.907 43.4404C87.907 45.5958 86.5713 47.4197 84.2338 47.4197C81.8963 47.4197 80.5605 45.5958 80.5605 43.4404C80.5605 41.285 81.8963 39.4611 84.2338 39.4611C86.5713 39.4611 87.907 41.285 87.907 43.4404ZM81.9797 43.4404C81.9797 44.9326 82.6476 46.342 84.3173 46.342C85.9869 46.342 86.6548 44.9326 86.6548 43.4404C86.6548 41.9482 85.9869 40.5388 84.3173 40.5388C82.6476 40.5388 81.9797 42.0311 81.9797 43.4404Z" fill="#231F20"/>
          <path d="M89.2422 39.6269H92.9154C94.5851 39.6269 95.4199 40.373 95.4199 41.6995C95.4199 43.1917 94.4181 43.5233 94.1677 43.6062C94.6685 43.6891 95.3364 44.0207 95.3364 45.1813C95.3364 46.0932 95.5034 46.8394 95.7538 47.171H94.3346C94.1677 46.8394 94.1677 46.4248 94.1677 46.0103C94.1677 44.601 93.9172 44.0207 92.665 44.0207H90.6614V47.2539H89.3257V39.6269H89.2422ZM90.5779 43.1088H92.7484C93.5833 43.1088 94.0842 42.6943 94.0842 41.8653C94.0842 40.8704 93.4163 40.7046 92.7484 40.7046H90.5779V43.1088Z" fill="#231F20"/>
          <path d="M97.7576 44.6839C97.7576 45.8445 98.5924 46.342 99.6777 46.342C100.846 46.342 101.264 45.7616 101.264 45.1813C101.264 44.601 100.93 44.3523 100.596 44.2694C100.095 44.1036 99.3438 43.9378 98.342 43.6062C97.0063 43.2746 96.6723 42.4456 96.6723 41.6995C96.6723 40.2072 98.0915 39.4611 99.5107 39.4611C101.097 39.4611 102.433 40.2901 102.433 41.8653H101.097C101.013 40.8704 100.346 40.5388 99.4272 40.5388C98.8429 40.5388 98.008 40.7875 98.008 41.5337C98.008 42.0311 98.342 42.3627 98.9263 42.5285C99.0098 42.5285 100.763 43.0259 101.18 43.1088C102.182 43.4404 102.683 44.2694 102.683 45.0155C102.683 46.7565 101.18 47.4197 99.5942 47.4197C97.8411 47.4197 96.4219 46.5907 96.4219 44.6839H97.7576Z" fill="#231F20"/>
          <path d="M103.936 39.6269H105.271V47.2539H103.936V39.6269Z" fill="#231F20"/>
          <path d="M107.023 39.6269H108.359V43.1088L111.782 39.6269H113.452L110.363 42.6943L113.619 47.2539H111.949L109.444 43.6062L108.359 44.6839V47.2539H107.023V39.6269Z" fill="#231F20"/>
          <path d="M114.454 39.6269H118.127C119.797 39.6269 120.632 40.373 120.632 41.6995C120.632 43.1917 119.63 43.5233 119.38 43.6062C119.88 43.6891 120.548 44.0207 120.548 45.1813C120.548 46.0932 120.715 46.8394 120.966 47.171H119.547C119.38 46.8394 119.38 46.4248 119.38 46.0103C119.38 44.601 119.129 44.0207 117.877 44.0207H115.873V47.2539H114.538V39.6269H114.454ZM115.79 43.1088H117.96C118.795 43.1088 119.296 42.6943 119.296 41.8653C119.296 40.8704 118.628 40.7046 117.96 40.7046H115.79V43.1088Z" fill="#231F20"/>
          <path d="M122.135 39.6269H123.47V47.2539H122.135V39.6269Z" fill="#231F20"/>
          <path d="M125.14 39.6269H126.559L130.065 45.2642V39.6269H131.317V47.2539H129.898L126.392 41.6166V47.2539H125.14V39.6269Z" fill="#231F20"/>
          <path d="M139.749 47.2539H138.914L138.747 46.342C137.996 47.171 137.411 47.4197 136.41 47.4197C134.072 47.4197 132.736 45.5958 132.736 43.4404C132.736 41.285 134.072 39.4611 136.41 39.4611C138.079 39.4611 139.498 40.373 139.665 42.0311H138.33C138.163 41.0363 137.328 40.5388 136.326 40.5388C134.656 40.5388 133.989 41.9482 133.989 43.4404C133.989 44.9326 134.656 46.342 136.326 46.342C137.662 46.342 138.497 45.5129 138.497 44.2694H136.493V43.2746H139.749V47.2539Z" fill="#231F20"/>
        </svg>
      )}
    </div>
  );

  if (!asLink) {
    return logoContent;
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title="Besøg Sygeforsikringen &quot;danmark&quot; (åbner i nyt vindue)"
      className="inline-block transition-opacity hover:opacity-85 focus:outline-hidden focus:ring-2 focus:ring-[#5f7365] focus:ring-offset-2 rounded-xs"
    >
      {logoContent}
    </a>
  );
}

/**
 * DanmarkTilskudCard
 * 
 * Fremhævet informationskort om tilskud fra Sygeforsikringen "danmark"
 * Designet med inspiration fra Klinik Corydon (klinikcorydon.dk),
 * afstemt efter Din Privat Psykologs visuelle identitet og farvepalette.
 */
export function DanmarkTilskudCard({ className = '' }) {
  return (
    <div
      className={`bg-white border border-[#5f7365] p-6 sm:p-8 lg:p-10 transition-shadow duration-200 ${className}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-center">
        
        {/* Venstre kolonne: Logo i ren hvid baggrundsramme */}
        <div className="md:col-span-4 flex flex-col items-center justify-center p-6 bg-white border border-[#EDE6D8] rounded-xs text-center shadow-2xs">
          <SygeforsikringDanmarkLogo
            className="w-48 max-w-full h-auto"
            href="https://www.sygeforsikring.dk"
          />
          <div className="mt-3 pt-3 border-t border-[#F2EDE2] w-full flex flex-col items-center">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#5f7365] uppercase tracking-wider">
              <i className="fa-regular fa-circle-check text-emerald-600" aria-hidden="true" />
              Elektronisk indberetning
            </span>
            <a
              href="https://www.sygeforsikring.dk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-[#7A6A5E] hover:text-[#5f7365] hover:underline mt-1 inline-flex items-center gap-1"
            >
              <span>www.sygeforsikring.dk</span>
              <i className="fa-regular fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
            </a>
          </div>
        </div>

        {/* Højre kolonne: Tekst og konkrete tilskudsinformationer */}
        <div className="md:col-span-8 space-y-4">
          <div>
            <span className="text-[11px] font-semibold uppercase tracking-widest text-[#52685B] block mb-1">
              Tilskudsordning
            </span>
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#5f7365] leading-snug">
              Tilskud fra Sygeforsikringen &quot;danmark&quot;
            </h3>
          </div>

          <p className="text-sm sm:text-base text-[#384A3F] leading-relaxed">
            Er du medlem af <strong>Sygeforsikringen &quot;danmark&quot;</strong> i Gruppe 1, Gruppe 2 eller Gruppe 5, kan du få tilskud til psykologbehandling hos mig.
          </p>

          {/* Faktabokse */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <div className="p-3.5 bg-white border border-[#EDE6D8] rounded-xs space-y-1">
              <div className="flex items-center gap-2 text-rose-900 font-bold text-sm">
                <i className="fa-regular fa-money-bill-transfer text-base" aria-hidden="true" />
                <span>300 kr. retur pr. samtale</span>
              </div>
              <p className="text-xs text-[#52685B] leading-relaxed">
                Udbetales for hver individuel session jf. &quot;danmarks&quot; gældende takster for autoriseret psykologhjælp.
              </p>
            </div>

            <div className="p-3.5 bg-white border border-[#EDE6D8] rounded-xs space-y-1">
              <div className="flex items-center gap-2 text-[#5f7365] font-bold text-sm">
                <i className="fa-regular fa-bolt text-base" aria-hidden="true" />
                <span>Automatisk indberetning</span>
              </div>
              <p className="text-xs text-[#52685B] leading-relaxed">
                Giv mig blot besked ved samtalestart – så indberetter jeg automatisk direkte, og pengene går direkte på din konto.
              </p>
            </div>
          </div>

          {/* Privat sundhedsforsikring note (ligesom på Klinik Corydon) */}
          <div className="pt-2 border-t border-[#E8E1D4] text-xs sm:text-sm text-[#465A4F] space-y-1.5">
            <p className="flex items-start gap-2">
              <i className="fa-regular fa-shield-heart text-[#5f7365] mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                <strong>Husk også privat sundhedsforsikring:</strong> Mange har en sundheds- eller ulykkesforsikring via arbejde eller pension, der dækker psykologhjælp helt eller delvist ved frit valg af autoriseret psykolog.
              </span>
            </p>
          </div>

          {/* Link ud til takster */}
          <div className="pt-1 flex flex-wrap items-center gap-4">
            <a
              href="https://www.sygeforsikring.dk/tilskud/psykologhjaelp"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-[#5f7365] hover:text-[#425247] hover:underline"
            >
              <span>Se gældende takster og betingelser på sygeforsikring.dk</span>
              <i className="fa-regular fa-arrow-right" aria-hidden="true" />
            </a>
          </div>
        </div>

      </div>
    </div>
  );
}

/**
 * DanmarkMiniCallout
 * 
 * Kompakt tilskudsbanner til brug på behandlingssider (f.eks. samtaleterapi og onlineterapi)
 */
export function DanmarkMiniCallout({ className = '' }) {
  return (
    <div
      className={`p-5 bg-[#FAF8F4] border border-[#E5DDD0] flex flex-col gap-3.5 ${className}`}
    >
      <div className="flex items-start gap-3.5">
        <div className="bg-white p-2 border border-[#EAE3D6] shrink-0 shadow-2xs">
          <SygeforsikringDanmarkLogo className="w-24 sm:w-28 h-auto" />
        </div>
        <div className="space-y-1 min-w-0">
          <h4 className="font-serif font-bold text-sm text-[#5f7365] leading-snug">
            Tilskud fra Sygeforsikringen &quot;danmark&quot;
          </h4>
          <p className="text-xs text-[#465A4F] leading-snug">
            Få <strong>300 kr. i tilskud pr. session</strong> ved medlemskab (Gruppe 1, 2 og 5). Der indberettes automatisk elektronisk.
          </p>
        </div>
      </div>

      <div className="pt-2.5 border-t border-[#EAE3D6] flex items-center justify-between text-xs">
        <span className="text-[11px] text-[#697D71]">Elektronisk indberetning</span>
        <Link
          to="/priser"
          className="font-semibold text-[#5f7365] hover:text-[#3B4D42] hover:underline whitespace-nowrap inline-flex items-center gap-1"
        >
          <span>Læs om priser og tilskud</span>
          <i className="fa-regular fa-arrow-right text-[11px]" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}

export default SygeforsikringDanmarkLogo;
